import React from "react";
import {
  MODULE_STRUCTURE,
  MODULE_DISPLAY_NAMES,
  hasModuleAccess,
  type ModuleName,
} from "../../lib/modules";

/**
 * Props for ModuleSelector.
 * @param value - Currently enabled module identifiers (controlled).
 * @param onChange - Callback invoked with (module, isEnabled) when a checkbox is toggled.
 * @param disabled - When true, all checkboxes are disabled (e.g. during a pending mutation).
 */
interface ModuleSelectorProps {
  value: string[];
  onChange: (module: ModuleName, isEnabled: boolean) => void;
  disabled?: boolean;
}

/**
 * Controlled checkbox list for selecting enabled modules per tenant.
 * Extracted from TenantManagement.tsx for reuse across admin pages.
 */
export const ModuleSelector: React.FC<ModuleSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handleWildcardToggle = (categoryKey: string, enabled: boolean): void => {
    const category = MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];
    if (!category || !category.wildcard) return;

    if (enabled) {
      onChange(category.wildcard as ModuleName, true);
    } else {
      onChange(category.wildcard as ModuleName, false);
      category.modules.forEach((module) => {
        if (value.includes(module)) {
          onChange(module as ModuleName, false);
        }
      });
    }
  };

  const handleIndividualModuleToggle = (module: string, enabled: boolean): void => {
    const [categoryKey] = module.split(":");
    const category = MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];

    if (enabled) {
      if (category?.wildcard && value.includes(category.wildcard)) {
        onChange(category.wildcard as ModuleName, false);
      }
      onChange(module as ModuleName, true);
    } else {
      onChange(module as ModuleName, false);
    }
  };

  const isWildcardEnabled = (categoryKey: string): boolean => {
    const category = MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];
    if (!category?.wildcard) return false;
    return value.includes(category.wildcard);
  };

  const isIndividualModuleEnabled = (module: string): boolean => {
    return hasModuleAccess(value, module);
  };

  return (
    <div className="space-y-4">
      {Object.entries(MODULE_STRUCTURE).map(([categoryKey, category]) => {
        const wildcardEnabled = isWildcardEnabled(categoryKey);
        const wildcardId = `module-wildcard-${categoryKey}`;

        return (
          <div key={categoryKey} className="space-y-2">
            {/* Category wildcard option */}
            <label
              htmlFor={wildcardId}
              className="flex items-center space-x-3 p-2 bg-neutral-50 rounded-md cursor-pointer"
            >
              <input
                id={wildcardId}
                type="checkbox"
                checked={wildcardEnabled}
                onChange={(e) =>
                  handleWildcardToggle(categoryKey, e.target.checked)
                }
                disabled={disabled}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <span className="text-sm font-medium text-neutral-700">
                {category.label} (wszystkie) *
              </span>
            </label>

            {/* Individual modules */}
            <div className="ml-6 space-y-2">
              {category.modules.map((module) => {
                const isEnabled = isIndividualModuleEnabled(module);
                const isDisabled = wildcardEnabled || disabled;
                const displayName =
                  MODULE_DISPLAY_NAMES[module] || module.split(":").pop();
                const inputId = `module-${module}`;

                return (
                  <label
                    key={module}
                    htmlFor={inputId}
                    className="flex items-center space-x-3 cursor-pointer"
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) =>
                        handleIndividualModuleToggle(module, e.target.checked)
                      }
                      disabled={isDisabled}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <span
                      className={`text-sm ${
                        isDisabled ? "text-neutral-400" : "text-neutral-700"
                      }`}
                    >
                      {displayName}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-neutral-500 mt-2">
        * Wybór opcji "wszystkie" automatycznie włącza wszystkie podmoduły w
        kategorii
      </div>
    </div>
  );
};

export default ModuleSelector;
